// CardProgressionSystem.ts — pure engine module, zero Phaser imports
// Handles card leveling via duplicate draws and skill tree node bonuses.

import type {
  TowerCard,
  TowerStats,
  UnitCard,
  Hero,
  OwnedCardData,
  SkillNode,
  SkillGate,
  CardSkillTree,
  Rarity,
} from '../interfaces/index.js'

// ── Level thresholds (total copies needed to reach each level) ────────────────
// Index 0 = Level 1 (need 1 copy to own it), index 9 = Level 10.
// Gaps grow exponentially to create meaningful long-term goals.
export const LEVEL_THRESHOLDS: Record<Rarity, number[]> = {
  common:    [1,   3,   7,  15,  30,  55,  95, 155, 245, 375],
  rare:      [1,   4,  10,  22,  44,  78, 125, 190, 290, 430],
  epic:      [1,   5,  14,  32,  65, 110, 175, 265, 390, 560],
  legendary: [1,   8,  22,  50,  95, 160, 250, 370, 530, 750],
}

export const MAX_CARD_LEVEL = 10

/** Gems required to reset a card's skill tree node choices. */
export const SKILL_RESET_COST_GEMS = 150

export class CardProgressionSystem {
  private static _instance: CardProgressionSystem | null = null

  /** Skill tree data keyed by card ID. */
  private towerTrees: Map<string, CardSkillTree> = new Map()
  private unitTrees: Map<string, CardSkillTree> = new Map()
  private heroTrees: Map<string, CardSkillTree> = new Map()

  static getInstance(): CardProgressionSystem {
    if (CardProgressionSystem._instance === null) {
      CardProgressionSystem._instance = new CardProgressionSystem()
    }
    return CardProgressionSystem._instance
  }

  /** Load skill tree data from the JSON config. Called once at startup. */
  loadTrees(data: {
    towers: CardSkillTree[]
    units: CardSkillTree[]
    heroes: CardSkillTree[]
  }): void {
    this.towerTrees = new Map(data.towers.map((t) => [t.id, t]))
    this.unitTrees  = new Map(data.units.map((u)  => [u.id, u]))
    this.heroTrees  = new Map(data.heroes.map((h) => [h.id, h]))
  }

  // ── Level calculation ────────────────────────────────────────────────────────

  getCardLevel(copies: number, rarity: Rarity = 'common'): number {
    const thresholds = LEVEL_THRESHOLDS[rarity]
    let level = 1
    for (let i = 0; i < thresholds.length; i++) {
      if (copies >= (thresholds[i] ?? Infinity)) level = i + 1
    }
    return level
  }

  /** How many copies to reach the next level (returns null if already max). */
  getCopiesToNextLevel(
    copies: number,
    rarity: Rarity = 'common',
  ): { needed: number; have: number } | null {
    const thresholds = LEVEL_THRESHOLDS[rarity]
    const currentLevel = this.getCardLevel(copies, rarity)
    if (currentLevel >= MAX_CARD_LEVEL) return null
    const nextThreshold = thresholds[currentLevel] ?? thresholds[thresholds.length - 1]!
    return { needed: nextThreshold, have: copies }
  }

  // ── Skill tree queries ───────────────────────────────────────────────────────

  private getTree(cardId: string, category: 'tower' | 'unit' | 'hero'): CardSkillTree | undefined {
    if (category === 'tower') return this.towerTrees.get(cardId)
    if (category === 'unit')  return this.unitTrees.get(cardId)
    return this.heroTrees.get(cardId)
  }

  /**
   * Returns the gate that should be shown to the player right now:
   * the lowest-level gate where the player has not yet made a choice,
   * but has reached the required card level, and all required parent nodes
   * from earlier gates are already chosen.
   */
  getPendingGate(
    cardId: string,
    category: 'tower' | 'unit' | 'hero',
    copies: number,
    rarity: Rarity,
    chosenNodes: string[],
  ): { gate: SkillGate; eligibleNodes: SkillNode[] } | null {
    const tree = this.getTree(cardId, category)
    if (tree === undefined) return null

    const level = this.getCardLevel(copies, rarity)
    const chosenSet = new Set(chosenNodes)

    for (const gate of tree.gates) {
      if (level < gate.level) break  // not high enough level yet
      if (gate.nodes.every((n) => n.auto === true)) continue  // auto-capstone, skip

      // Check if any non-auto node in this gate has been chosen
      const nonAutoNodes = gate.nodes.filter((n) => !n.auto)
      const gateAlreadyChosen = nonAutoNodes.some((n) => chosenSet.has(n.id))
      if (gateAlreadyChosen) continue  // already decided for this gate

      // Find eligible nodes (requires parent is chosen, or no parent)
      const eligible = nonAutoNodes.filter(
        (n) => n.requires === null || chosenSet.has(n.requires),
      )
      if (eligible.length === 0) continue  // parent not chosen yet

      return { gate, eligibleNodes: eligible }
    }
    return null
  }

  /**
   * Returns all capstone (auto) nodes that should be automatically activated
   * given the current level and chosen nodes.
   */
  getAutoNodes(
    cardId: string,
    category: 'tower' | 'unit' | 'hero',
    copies: number,
    rarity: Rarity,
    chosenNodes: string[],
  ): SkillNode[] {
    const tree = this.getTree(cardId, category)
    if (tree === undefined) return []

    const level = this.getCardLevel(copies, rarity)
    const chosenSet = new Set(chosenNodes)
    const result: SkillNode[] = []

    for (const gate of tree.gates) {
      if (level < gate.level) break
      for (const node of gate.nodes) {
        if (node.auto === true && !chosenSet.has(node.id)) {
          result.push(node)
        }
      }
    }
    return result
  }

  /** Get all currently active nodes (chosen + auto-unlocked). */
  getActiveNodes(
    cardId: string,
    category: 'tower' | 'unit' | 'hero',
    copies: number,
    rarity: Rarity,
    chosenNodes: string[],
  ): SkillNode[] {
    const tree = this.getTree(cardId, category)
    if (tree === undefined) return []

    const level = this.getCardLevel(copies, rarity)
    const chosenSet = new Set(chosenNodes)
    const result: SkillNode[] = []

    for (const gate of tree.gates) {
      if (level < gate.level) break
      for (const node of gate.nodes) {
        if (chosenSet.has(node.id) || node.auto === true) {
          result.push(node)
        }
      }
    }
    return result
  }

  // ── Stat computation ─────────────────────────────────────────────────────────

  /**
   * Compute effective TowerStats for a placed tower, including level bonuses and
   * all active skill tree node bonuses.
   */
  getEffectiveTowerStats(
    card: TowerCard,
    ownedData: OwnedCardData | undefined,
  ): TowerStats {
    if (ownedData === undefined || ownedData.copies === 0) return { ...card.stats }

    const tree = this.towerTrees.get(card.id)
    const level = this.getCardLevel(ownedData.copies, card.rarity)
    const levelUps = level - 1  // number of level-up events

    // Base level bonuses
    const dmgLevelMult    = 1 + (tree?.levelBonuses.dmgPerLevel ?? 0) * levelUps
    const spdLevelMult    = 1 + (tree?.levelBonuses.spdPerLevel ?? 0) * levelUps
    const rangeLevelMult  = 1 + (tree?.levelBonuses.rangePerLevel ?? 0) * levelUps

    // Active node bonuses
    const activeNodes = this.getActiveNodes(card.id, 'tower', ownedData.copies, card.rarity, ownedData.chosenNodes)
    let dmgNodeBonus   = 0
    let spdNodeBonus   = 0
    let rangeNodeBonus = 0
    let critNodeBonus  = 0
    let chainNodeBonus = 0
    let splashNodeBonus = 0
    let slowNodeBonus   = 0
    let incomeNodeBonus = 0

    for (const node of activeNodes) {
      dmgNodeBonus    += node.effects.dmgBonus    ?? 0
      spdNodeBonus    += node.effects.spdBonus    ?? 0
      rangeNodeBonus  += node.effects.rangeBonus  ?? 0
      critNodeBonus   += node.effects.critBonus   ?? 0
      chainNodeBonus  += node.effects.chainBonus  ?? 0
      splashNodeBonus += node.effects.splashBonus ?? 0
      slowNodeBonus   += node.effects.slowBonus   ?? 0
      incomeNodeBonus += node.effects.incomeBonus ?? 0
    }

    const base = card.stats
    return {
      dmg:       base.dmg   * dmgLevelMult   * (1 + dmgNodeBonus),
      spd:       base.spd   * spdLevelMult   * (1 + spdNodeBonus),
      range:     base.range * rangeLevelMult * (1 + rangeNodeBonus),
      crit:      Math.min(0.95, base.crit + critNodeBonus),
      chain:     (base.chain ?? 0) + chainNodeBonus,
      splash:    base.splash !== undefined ? base.splash * (1 + splashNodeBonus) : undefined,
      slow:      base.slow   !== undefined ? Math.min(0.95, base.slow + slowNodeBonus) : undefined,
      income:    base.income !== undefined ? base.income * (1 + (tree?.levelBonuses.incomePerLevel ?? 0) * levelUps) + incomeNodeBonus : undefined,
      terrain:   base.terrain,
      projSpeed: base.projSpeed,
    }
  }

  /**
   * Compute effective UnitCard stats including level and skill tree bonuses.
   * Returns a new UnitCard with boosted stats (caller must NOT mutate original).
   */
  getEffectiveUnitStats(
    unit: UnitCard,
    ownedData: OwnedCardData | undefined,
  ): UnitCard {
    if (ownedData === undefined || ownedData.copies === 0) return { ...unit }

    const tree = this.unitTrees.get(unit.id)
    const level = this.getCardLevel(ownedData.copies, unit.rarity)
    const levelUps = level - 1

    const hpLevelMult  = 1 + (tree?.levelBonuses.hpPerLevel ?? 0) * levelUps
    const spdLevelMult = 1 + (tree?.levelBonuses.spdPerLevel ?? 0) * levelUps

    const activeNodes = this.getActiveNodes(unit.id, 'unit', ownedData.copies, unit.rarity, ownedData.chosenNodes)
    let hpBonus     = 0
    let armorBonus  = 0
    let spdBonus    = 0
    let dodgeBonus  = 0
    let incomeBonus = 0

    for (const node of activeNodes) {
      hpBonus     += node.effects.hpBonus     ?? 0
      armorBonus  += node.effects.armorBonus  ?? 0
      spdBonus    += node.effects.spdBonus    ?? 0
      dodgeBonus  += node.effects.dodgeBonus  ?? 0
      incomeBonus += node.effects.incomeBonus ?? 0
    }

    return {
      ...unit,
      hp:     Math.round(unit.hp    * hpLevelMult  * (1 + hpBonus)),
      spd:    unit.spd * spdLevelMult * (1 + spdBonus),
      armor:  Math.min(0.95, unit.armor + armorBonus),
      dodge:  Math.min(0.95, unit.dodge + dodgeBonus),
      income: unit.income + incomeBonus,
    }
  }

  /**
   * Returns the enhanced passive buff value for a hero based on level and nodes.
   * The multiplied value is the hero's passivBuff.value after progression.
   */
  getEffectiveHeroPassiveValue(
    hero: Hero,
    ownedData: OwnedCardData | undefined,
  ): number {
    if (ownedData === undefined || ownedData.copies === 0) return hero.passivBuff.value

    const tree = this.heroTrees.get(hero.id)
    const level = this.getCardLevel(ownedData.copies, 'legendary')
    const levelUps = level - 1

    const levelEnhance = (tree?.levelBonuses.passivEnhancePerLevel ?? 0) * levelUps

    const activeNodes = this.getActiveNodes(hero.id, 'hero', ownedData.copies, 'legendary', ownedData.chosenNodes)
    let nodeEnhance = 0
    for (const node of activeNodes) {
      nodeEnhance += node.effects.passivEnhance ?? 0
    }

    return hero.passivBuff.value * (1 + levelEnhance + nodeEnhance)
  }

  /** Returns the effective cooldown for a hero's active spell after progression. */
  getEffectiveHeroCooldown(
    hero: Hero,
    ownedData: OwnedCardData | undefined,
  ): number {
    if (ownedData === undefined || ownedData.copies === 0) return hero.cooldownSec

    const tree = this.heroTrees.get(hero.id)
    const level = this.getCardLevel(ownedData.copies, 'legendary')
    const levelUps = level - 1

    const levelReduction = (tree?.levelBonuses.cooldownReductionPerLevel ?? 0) * levelUps

    const activeNodes = this.getActiveNodes(hero.id, 'hero', ownedData.copies, 'legendary', ownedData.chosenNodes)
    let nodeReduction = 0
    for (const node of activeNodes) {
      nodeReduction += node.effects.cooldownReduction ?? 0
    }

    return Math.max(5, hero.cooldownSec - levelReduction - nodeReduction)
  }

  // ── Utility ──────────────────────────────────────────────────────────────────

  getSkillGates(cardId: string, category: 'tower' | 'unit' | 'hero'): SkillGate[] {
    return this.getTree(cardId, category)?.gates ?? []
  }

  getLevelBonusSummary(
    cardId: string,
    category: 'tower' | 'unit' | 'hero',
    level: number,
  ): string[] {
    const tree = this.getTree(cardId, category)
    if (tree === undefined || level <= 1) return []
    const lb = tree.levelBonuses
    const lvl = level - 1
    const lines: string[] = []
    if (lb.dmgPerLevel)              lines.push(`+${Math.round(lb.dmgPerLevel * lvl * 100)}% Schaden`)
    if (lb.spdPerLevel)              lines.push(`+${Math.round(lb.spdPerLevel * lvl * 100)}% Angriffsgeschwindigkeit`)
    if (lb.rangePerLevel)            lines.push(`+${Math.round(lb.rangePerLevel * lvl * 100)}% Reichweite`)
    if (lb.hpPerLevel)               lines.push(`+${Math.round(lb.hpPerLevel * lvl * 100)}% HP`)
    if (lb.armorPerLevel)            lines.push(`+${(lb.armorPerLevel * lvl).toFixed(2)} Rüstung`)
    if (lb.incomePerLevel)           lines.push(`+${(lb.incomePerLevel * lvl).toFixed(1)} Gold/Sek`)
    if (lb.passivEnhancePerLevel)    lines.push(`+${Math.round(lb.passivEnhancePerLevel * lvl * 100)}% Passive Stärkung`)
    if (lb.cooldownReductionPerLevel) lines.push(`-${(lb.cooldownReductionPerLevel * lvl).toFixed(0)}s Cooldown`)
    return lines
  }
}
